import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CustomerService } from '../../../Services/customer.service';
import { Customer } from '../../../Models/customer.model';
import {
  Auftrag,
  AUFTRAG_STATUS_LABELS,
  AuftragStatus,
  MyStammkundeMembership,
  StammkundeMember,
  StammkundeWorker,
  WarehouseRef
} from '../../../Models/stammkunde.model';
import { AuthService } from '../../../api-authorization/authorize.service';
import { AuftragFormComponent } from '../auftrag-form/auftrag-form.component';
import { CustomerFormComponent } from '../../Wartung/customer-form/customer-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { TeamFormComponent } from '../team-form/team-form.component';
import { WorkerFormComponent } from '../worker-form/worker-form.component';
import { LagerLinkComponent } from '../lager-link/lager-link.component';

type AuftragFilter = 'alle' | 'offen' | 'fertig';

/** Aufträge mit diesen Status gelten als "fertig" (Rest = offen). */
const DONE_STATUSES: AuftragStatus[] = ['Completed', 'Priced', 'Billed'];

/**
 * Detailseite eines Stammkunden: Kennzahlen, Aufträge, Team (inkl. Arbeiter ohne
 * Login), verknüpfte Lager und Stammdaten. Alle Pflege-Aktionen laufen über Popups.
 */
@Component({
  selector: 'app-stammkunde-detail',
  templateUrl: './stammkunde-detail.component.html',
  styleUrls: ['./stammkunde-detail.component.css', '../stammkunden-theme.css']
})
export class StammkundeDetailComponent implements OnInit {
  customerId!: number;
  customer: Customer | null = null;
  auftraege: Auftrag[] = [];
  members: StammkundeMember[] = [];
  workers: StammkundeWorker[] = [];
  linkedWarehouses: WarehouseRef[] = [];
  myMemberships: MyStammkundeMembership[] = [];

  loading = true;
  selectedTab = 0;
  auftragFilter: AuftragFilter = 'alle';

  readonly statusLabels = AUFTRAG_STATUS_LABELS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stammkundeService: StammkundeService,
    private customerService: CustomerService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.customerId = Number(params.get('id'));
      this.load();
    });
  }

  // ── Rechte ──

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  /** Bauleiter DIESES Stammkunden (laut Mitgliedschaft) oder Admin/SuperAdmin. */
  get canManage(): boolean {
    if (this.authService.isSuperAdmin() || this.authService.isAdmin()) return true;
    return this.myMemberships.some(m => m.customerId === this.customerId && m.role === 'Bauleiter');
  }

  /** Der Manager bekommt zusätzlich einen Übersicht-Tab (Freigaben/Auswertung) an Position 0. */
  get hasOverviewTab(): boolean {
    return this.canManage;
  }

  // Tab-Indizes verschieben sich, wenn der Übersicht-Tab vorangestellt ist.
  get tabAuftraege(): number { return this.hasOverviewTab ? 1 : 0; }
  get tabTeam(): number { return this.hasOverviewTab ? 2 : 1; }
  get tabLager(): number { return this.hasOverviewTab ? 3 : 2; }

  // ── Laden ──

  load(): void {
    this.loading = true;
    forkJoin({
      customer: this.customerService.getCustomer(this.customerId),
      auftraege: this.stammkundeService.getAuftraege(this.customerId),
      members: this.stammkundeService.getMembers(this.customerId),
      workers: this.stammkundeService.getWorkers(this.customerId),
      warehouses: this.stammkundeService.getLinkedWarehouses(this.customerId),
      permissions: this.authService.getMyPermissions()
    }).subscribe({
      next: result => {
        this.customer = result.customer;
        this.auftraege = result.auftraege ?? [];
        this.members = result.members ?? [];
        this.workers = result.workers ?? [];
        this.linkedWarehouses = result.warehouses ?? [];
        this.myMemberships = result.permissions?.stammkundenMemberships ?? [];
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden des Stammkunden', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  // ── Kennzahlen ──

  get offeneAuftraege(): number {
    return this.auftraege.filter(a => !DONE_STATUSES.includes(a.status)).length;
  }

  get gesamtEinsaetze(): number {
    return this.auftraege.reduce((sum, a) => sum + (a.assignmentCount ?? 0), 0);
  }

  get teamGroesse(): number {
    return this.members.length + this.workers.length;
  }

  goTab(index: number): void {
    this.selectedTab = index;
  }

  // ── Aufträge ──

  get filteredAuftraege(): Auftrag[] {
    switch (this.auftragFilter) {
      case 'offen': return this.auftraege.filter(a => !DONE_STATUSES.includes(a.status));
      case 'fertig': return this.auftraege.filter(a => DONE_STATUSES.includes(a.status));
      default: return this.auftraege;
    }
  }

  countFor(filter: AuftragFilter): number {
    switch (filter) {
      case 'offen': return this.offeneAuftraege;
      case 'fertig': return this.auftraege.length - this.offeneAuftraege;
      default: return this.auftraege.length;
    }
  }

  openAuftragForm(auftrag?: Auftrag): void {
    const dialogRef = this.dialog.open(AuftragFormComponent, {
      width: '760px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, auftrag, linkedWarehouses: this.linkedWarehouses }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.reloadAuftraege();
        this.snackBar.open(auftrag ? 'Auftrag aktualisiert' : 'Auftrag angelegt', 'OK', { duration: 3000 });
      }
    });
  }

  openAuftrag(auftrag: Auftrag): void {
    this.router.navigate(['/stammkunden', this.customerId, 'auftrag', auftrag.id]);
  }

  openPlanung(): void {
    this.router.navigate(['/stammkunden', this.customerId, 'planung']);
  }

  editAuftrag(auftrag: Auftrag, event: Event): void {
    event.stopPropagation();
    this.openAuftragForm(auftrag);
  }

  deleteAuftrag(auftrag: Auftrag, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: {
        title: 'Auftrag löschen',
        message: `"${auftrag.title}" wirklich löschen? Die Einsätze bleiben erhalten, verlieren aber den Auftragsbezug.`
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.deleteAuftrag(auftrag.id).subscribe({
          next: () => {
            this.reloadAuftraege();
            this.snackBar.open('Auftrag gelöscht', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  private reloadAuftraege(): void {
    this.stammkundeService.getAuftraege(this.customerId).subscribe({
      next: auftraege => this.auftraege = auftraege ?? [],
      error: () => { /* Liste bleibt unverändert */ }
    });
  }

  statusLabel(status: AuftragStatus): string {
    return this.statusLabels[status] ?? status;
  }

  // ── Team (Popups) ──

  openTeamDialog(): void {
    const dialogRef = this.dialog.open(TeamFormComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, members: this.members }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.snackBar.open('Team gespeichert', 'OK', { duration: 3000 });
        this.stammkundeService.getMembers(this.customerId).subscribe({
          next: members => this.members = members ?? [],
          error: () => { /* Anzeige bleibt unverändert */ }
        });
      }
    });
  }

  openWorkerDialog(worker?: StammkundeWorker): void {
    const dialogRef = this.dialog.open(WorkerFormComponent, {
      width: '460px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, worker }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.snackBar.open(worker ? 'Arbeiter aktualisiert' : 'Arbeiter angelegt', 'OK', { duration: 3000 });
        this.stammkundeService.getWorkers(this.customerId).subscribe({
          next: workers => this.workers = workers ?? [],
          error: () => { /* Anzeige bleibt unverändert */ }
        });
      }
    });
  }

  deleteWorker(worker: StammkundeWorker, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Arbeiter entfernen',
        message: `"${worker.name}" wirklich entfernen? Er wird auch aus bereits geplanten Einsätzen entfernt.`
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.deleteWorker(this.customerId, worker.id).subscribe({
          next: () => {
            this.workers = this.workers.filter(w => w.id !== worker.id);
            this.snackBar.open('Arbeiter entfernt', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Entfernen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  // ── Lager (Popup) ──

  openLagerDialog(): void {
    const dialogRef = this.dialog.open(LagerLinkComponent, {
      width: '520px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, linked: this.linkedWarehouses }
    });
    dialogRef.afterClosed().subscribe(linked => {
      if (linked) {
        this.snackBar.open('Lager verknüpft', 'OK', { duration: 3000 });
        this.reloadWarehouses();
      }
    });
  }

  unlinkWarehouse(warehouse: WarehouseRef): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Verknüpfung lösen',
        message: `Verknüpfung mit "${warehouse.name}" wirklich lösen?`
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.unlinkWarehouse(this.customerId, warehouse.id).subscribe({
          next: () => {
            this.reloadWarehouses();
            this.snackBar.open('Verknüpfung gelöst', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Lösen der Verknüpfung', 'OK', { duration: 4000 })
        });
      }
    });
  }

  private reloadWarehouses(): void {
    this.stammkundeService.getLinkedWarehouses(this.customerId).subscribe({
      next: warehouses => this.linkedWarehouses = warehouses ?? [],
      error: () => { /* Anzeige bleibt unverändert */ }
    });
  }

  // ── Stammdaten ──

  editCustomer(): void {
    if (!this.customer) return;
    const dialogRef = this.dialog.open(CustomerFormComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: { ...this.customer }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.load();
        this.snackBar.open('Stammdaten aktualisiert', 'OK', { duration: 3000 });
      }
    });
  }

  openWartungDetail(): void {
    this.router.navigate(['/wartung/kunden', this.customerId]);
  }

  getLogoUrl(logoPath: string): string {
    return this.customerService.getLogoUrl(logoPath);
  }

  back(): void {
    this.router.navigate(['/stammkunden']);
  }
}
